import { StateManager } from '../core/StateManager';
import { DRIVE_PROFILES } from '../config/profiles';

export class Oscilloscope {
  private ctx: CanvasRenderingContext2D;
  private phaseAngle = 0;
  private animId = 0;
  private running = false;
  private waveColor = '#00ff9d';

  constructor(
    private canvas: HTMLCanvasElement,
    private state: StateManager,
  ) {
    this.ctx = canvas.getContext('2d')!;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.waveColor =
      getComputedStyle(document.documentElement).getPropertyValue('--neon-ok').trim() || '#00ff9d';
    const loop = (): void => {
      this.draw();
      this.animId = requestAnimationFrame(loop);
    };
    this.animId = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.animId);
  }

  private draw(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;

    const width = (this.canvas.width = parent.clientWidth);
    const height = (this.canvas.height = parent.clientHeight);

    this.ctx.clearRect(0, 0, width, height);

    this.drawGrid(width, height);

    const profileKey = this.state.get('selectedProfileKey');
    const profile = DRIVE_PROFILES[profileKey];
    if (!profile) return;
    const currentAmp = this.state.get('currentAmp');
    const currentFreq = this.state.get('currentFreq');
    const looseTerminal = this.state.get('looseTerminal');

    this.drawCurrentWave(width, height, currentAmp, currentFreq, profile.nominalCurrent);
    this.drawRippleWave(width, height, looseTerminal);

    this.phaseAngle += 0.08;
  }

  private drawGrid(width: number, height: number): void {
    this.ctx.strokeStyle = '#0e1626';
    this.ctx.lineWidth = 1;
    for (let x = 0; x < width; x += 30) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, height);
      this.ctx.stroke();
    }
    for (let y = 0; y < height; y += 30) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(width, y);
      this.ctx.stroke();
    }
  }

  private drawCurrentWave(
    width: number,
    height: number,
    currentAmp: number,
    currentFreq: number,
    nominalCurrent: number,
  ): void {
    this.ctx.beginPath();
    this.ctx.strokeStyle = this.waveColor;
    this.ctx.lineWidth = 2;

    const ampScale = (currentAmp / nominalCurrent) * (height / 3.5);
    const freqScale = (currentFreq / 50.0) * 0.05;

    for (let x = 0; x < width; x++) {
      const y = height / 2 + Math.sin(x * freqScale + this.phaseAngle) * ampScale;
      if (x === 0) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
  }

  private drawRippleWave(width: number, height: number, looseTerminal: boolean): void {
    this.ctx.beginPath();
    this.ctx.strokeStyle = '#00d9ff';
    this.ctx.lineWidth = 1.5;

    const rippleAmp = looseTerminal ? 18 : 3;

    for (let x = 0; x < width; x++) {
      const noise = (Math.random() - 0.5) * (looseTerminal ? 8 : 1);
      const y = height / 4 + Math.sin(x * 0.25 + this.phaseAngle * 2) * rippleAmp + noise;
      if (x === 0) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
  }
}
